import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageContainer, Button, ErrorBoundary, Pagination, Select, Skeleton, EmptyState, useToast } from '@bbpos/ui';
import { productMasterService } from '@/services/product-master.service';
import { GLASS, GLASS_FILTER, cn } from '@/constants/design-tokens';
import type { ConflictAction, MasterConflict, MasterConflictType } from '@/types/product-master';

const PAGE_SIZE = 100;

export const CONFLICT_LABELS: Record<MasterConflictType, string> = {
  NAME: 'Name',
  BARCODE: 'Barcode',
  VARIANT_ROLE: 'Variant or not',
  VARIANT_SET: 'Missing variant',
};

const CONFLICT_TONES: Record<MasterConflictType, string> = {
  NAME: 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300',
  BARCODE: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  VARIANT_ROLE: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  VARIANT_SET: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
};

/** The two ways to settle each kind of conflict, in button order (plan §6, D6). */
export const CONFLICT_ACTIONS: Record<MasterConflictType, Array<{ action: ConflictAction; label: string }>> = {
  NAME: [
    { action: 'USE_MASTER', label: 'Use master' },
    { action: 'USE_STORE', label: 'Use this store' },
  ],
  BARCODE: [
    { action: 'USE_MASTER', label: 'Use master' },
    { action: 'USE_STORE', label: 'Use this store' },
  ],
  VARIANT_SET: [
    { action: 'ADD_MISSING_VARIANTS', label: 'Add the missing variants here' },
    { action: 'KEEP_STORE_SUBSET', label: "Keep this store's set" },
  ],
  VARIANT_ROLE: [
    { action: 'MATCH_MASTER', label: 'Make this store match the master' },
    { action: 'KEEP_SEPARATE', label: 'Keep as a separate product' },
  ],
};

/** How the backfill names a row's place in a variant family. */
const ROLE_LABELS: Record<string, string> = {
  VARIANT: 'A variant of another product',
  VARIANT_NO_PARENT: 'A variant with no main product',
  PRODUCT: 'A product of its own',
};

const SUCCESS: Record<ConflictAction, (c: MasterConflict) => string> = {
  USE_MASTER: (c) =>
    `${c.location_name ?? 'The store'} now uses the master ${CONFLICT_LABELS[c.conflict_type].toLowerCase()}`,
  USE_STORE: (c) =>
    `Every store now uses the ${CONFLICT_LABELS[c.conflict_type].toLowerCase()} from ${c.location_name ?? 'this store'}`,
  ADD_MISSING_VARIANTS: (c) => `${c.location_name ?? 'The store'} now sells every variant`,
  KEEP_STORE_SUBSET: (c) => `${c.location_name ?? 'The store'} keeps its own set of variants`,
  MATCH_MASTER: (c) => `${c.location_name ?? 'The store'} now matches the master`,
  KEEP_SEPARATE: (c) => `${c.location_name ?? 'The store'} keeps it as a separate product`,
};

/**
 * One card per thing to decide. A store that lacks several variants of a
 * product has one VARIANT_SET row per variant; they are settled together, so
 * they show as one card listing the variants (`missing`).
 */
export function conflictItems(conflicts: MasterConflict[]): Array<{ conflict: MasterConflict; missing: string[] }> {
  const items: Array<{ conflict: MasterConflict; missing: string[] }> = [];
  const byStoreRow = new Map<string, { conflict: MasterConflict; missing: string[] }>();
  for (const c of conflicts) {
    if (c.conflict_type !== 'VARIANT_SET') {
      items.push({ conflict: c, missing: [] });
      continue;
    }
    let item = byStoreRow.get(c.product_id);
    if (!item) {
      item = { conflict: c, missing: [] };
      byStoreRow.set(c.product_id, item);
      items.push(item);
    }
    if (c.master_value) item.missing.push(c.master_value);
  }
  return items;
}

export interface ConflictGroup {
  masterId: string;
  sku: string;
  masterName: string;
  conflicts: MasterConflict[];
}

/** One group per product, in the order the API sends them (it keeps a product's conflicts together). */
export function groupConflicts(rows: MasterConflict[]): ConflictGroup[] {
  const groups = new Map<string, ConflictGroup>();
  for (const row of rows) {
    let group = groups.get(row.master_id);
    if (!group) {
      group = { masterId: row.master_id, sku: row.sku, masterName: row.master_name, conflicts: [] };
      groups.set(row.master_id, group);
    }
    group.conflicts.push(row);
  }
  return [...groups.values()];
}

function Value({ label, value, tone }: { label: string; value: string | null; tone: string }) {
  return (
    <div className={cn('min-w-0 rounded-lg px-3 py-2', tone)}>
      <p className="text-[11px] font-semibold uppercase tracking-wider opacity-70">{label}</p>
      <p className="break-words text-sm font-medium">{value ?? '(none)'}</p>
    </div>
  );
}

function MasterConflictsContent() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page') ?? '1');
  const type = (searchParams.get('type') ?? '') as MasterConflictType | '';
  const [pendingId, setPendingId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['product-masters', 'conflicts', page, type],
    queryFn: () =>
      productMasterService.listConflicts({ page, limit: PAGE_SIZE, conflict_type: type || undefined }),
  });

  const resolve = useMutation({
    mutationFn: ({ conflict, action }: { conflict: MasterConflict; action: ConflictAction }) =>
      productMasterService.resolveConflict(conflict.id, action),
    onMutate: ({ conflict }) => setPendingId(conflict.id),
    onSuccess: (_res, { conflict, action }) => {
      toast({ title: SUCCESS[action](conflict), variant: 'success' });
    },
    // A row that cannot move (stock, batches) says why; "Keep as a separate product" stays open.
    onError: (err: Error) => toast({ title: err.message || 'Could not resolve this conflict', variant: 'error' }),
    onSettled: (_res, _err, { conflict }) => {
      setPendingId(null);
      queryClient.invalidateQueries({ queryKey: ['product-masters'] });
      queryClient.invalidateQueries({ queryKey: ['product-master', conflict.master_id] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product'] });
    },
  });

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    if (key !== 'page') params.delete('page');
    setSearchParams(params);
  }

  const groups = groupConflicts(data?.data ?? []);
  const total = data?.meta?.total ?? 0;

  return (
    <>
      <div className="mb-6 rounded-xl bg-gradient-to-r from-teal-600 to-amber-500 p-6 text-white shadow-lg animate-fade-in">
        <h2 className="text-2xl font-bold text-white">Conflicts to review</h2>
        <p className="mt-1 text-teal-50">
          Some stores had a different name, barcode or set of variants for the same product. Pick what to keep.
        </p>
        <p className="mt-3 inline-flex rounded-full bg-white/20 px-3 py-1 text-sm font-semibold tabular-nums">
          {total.toLocaleString('en-IN')} open
        </p>
      </div>

      <div className={cn(GLASS_FILTER, 'mb-6 p-3')}>
        <div className="w-full sm:w-56">
          <Select
            options={[
              { value: '', label: 'All kinds' },
              ...(Object.keys(CONFLICT_LABELS) as MasterConflictType[]).map((t) => ({ value: t, label: CONFLICT_LABELS[t] })),
            ]}
            value={type}
            onChange={(v) => setParam('type', typeof v === 'string' ? v : '')}
            placeholder="Kind"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={96} />
          ))}
        </div>
      ) : error ? (
        <div className={cn(GLASS, 'p-6 text-center text-sm text-danger-600 dark:text-danger-400')}>
          Could not load the conflicts. Please try again.
        </div>
      ) : groups.length === 0 ? (
        <EmptyState
          title="No conflicts to review"
          description="Every store agrees with the master catalogue."
          variant="page"
        />
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <section
              key={group.masterId}
              aria-label={group.masterName}
              className={cn(GLASS, 'overflow-hidden shadow-md transition-all duration-200 hover:shadow-lg animate-slide-up')}
            >
              <header className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-100 bg-gradient-to-r from-teal-50 to-emerald-50 px-4 py-3 dark:border-neutral-700 dark:from-teal-900/20 dark:to-emerald-900/10">
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-semibold text-neutral-900 dark:text-white">{group.masterName}</h3>
                  <p className="text-xs tabular-nums text-neutral-500 dark:text-neutral-400">{group.sku}</p>
                </div>
                <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                  {conflictItems(group.conflicts).length} to review
                </span>
              </header>
              <ul role="list" className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {conflictItems(group.conflicts).map(({ conflict: c, missing }) => (
                  <li
                    key={c.id}
                    data-testid={`conflict-${c.id}`}
                    className="space-y-3 px-4 py-3 transition-all duration-200 even:bg-neutral-50 hover:bg-brand-50 dark:even:bg-neutral-800/40 dark:hover:bg-brand-900/20"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', CONFLICT_TONES[c.conflict_type])}>
                        {CONFLICT_LABELS[c.conflict_type]}
                      </span>
                      <Link
                        to={`/products/${c.product_id}`}
                        className="text-sm font-medium text-neutral-700 hover:text-teal-600 hover:underline dark:text-neutral-200 dark:hover:text-teal-400"
                      >
                        {c.location_name ?? 'Store'}
                      </Link>
                    </div>
                    {c.conflict_type === 'VARIANT_SET' ? (
                      <Value
                        label={`Variants this store does not sell (${missing.length})`}
                        value={missing.join(', ')}
                        tone="bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-100"
                      />
                    ) : (
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <Value
                          label="Master"
                          value={c.conflict_type === 'VARIANT_ROLE' ? (ROLE_LABELS[c.master_value ?? ''] ?? c.master_value) : c.master_value}
                          tone="bg-teal-50 text-teal-900 dark:bg-teal-900/20 dark:text-teal-100"
                        />
                        <Value
                          label="This store"
                          value={c.conflict_type === 'VARIANT_ROLE' ? (ROLE_LABELS[c.store_value ?? ''] ?? c.store_value) : c.store_value}
                          tone="bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-100"
                        />
                      </div>
                    )}
                    {c.conflict_type === 'VARIANT_ROLE' && (
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        A row that still holds stock cannot move. Keep it as a separate product: its SKU gets the store code added.
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {CONFLICT_ACTIONS[c.conflict_type].map(({ action, label }, i) => (
                        <Button
                          key={action}
                          size="sm"
                          variant={i === 0 ? 'primary' : 'secondary'}
                          loading={pendingId === c.id && resolve.variables?.action === action}
                          disabled={pendingId !== null}
                          onClick={() => resolve.mutate({ conflict: c, action })}
                        >
                          {label}
                        </Button>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="mt-6">
          <Pagination
            currentPage={page}
            totalPages={data?.meta?.totalPages ?? 1}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={(p) => setParam('page', String(p))}
          />
        </div>
      )}
    </>
  );
}

export function MasterConflictsPage() {
  return (
    <PageContainer
      title="Catalogue Conflicts"
      subtitle="Stores that disagree with the master catalogue"
      breadcrumbs={[
        { label: 'Products', href: '/products' },
        { label: 'Master Catalogue', href: '/products/masters' },
        { label: 'Conflicts' },
      ]}
    >
      <ErrorBoundary>
        <MasterConflictsContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
